/**
 * Pure helpers behind `/tools/encode/url`. Tests in
 * `tests/web/urlCodecTool.test.ts`.
 *
 * Two modes:
 *   - 'component': escape every character that has special meaning in
 *     a URL component (incl. '&', '=', '/', '?'). Wraps
 *     `encodeURIComponent` / `decodeURIComponent`.
 *   - 'uri': preserve URI structural characters and only escape
 *     characters that aren't allowed anywhere in a URL. Wraps
 *     `encodeURI` / `decodeURI`.
 *
 * Plus a `splitQuery` / `buildQuery` pair that takes the pain out of
 * rebuilding URL query strings with the right escaping.
 */

export type UrlMode = 'component' | 'uri';

export type UrlResult =
  | { ok: true; output: string }
  | { ok: false; error: string };

export function encodeUrl(input: string, mode: UrlMode): UrlResult {
  if (input.length === 0) return { ok: true, output: '' };
  try {
    return {
      ok: true,
      output: mode === 'component' ? encodeURIComponent(input) : encodeURI(input),
    };
  } catch (e) {
    // Both encodeURI* throw URIError on lone surrogates only — rare in
    // practice but we surface a friendly message instead of crashing.
    return { ok: false, error: explainUriError(e) };
  }
}

export function decodeUrl(input: string, mode: UrlMode): UrlResult {
  if (input.length === 0) return { ok: true, output: '' };
  try {
    return {
      ok: true,
      output: mode === 'component' ? decodeURIComponent(input) : decodeURI(input),
    };
  } catch (e) {
    return { ok: false, error: explainUriError(e) };
  }
}

/**
 * Best-effort split of a URL into `{ origin, path, query }` so a UI
 * can highlight the part the user is iterating on.
 *
 * Returns null on bare query strings ('?a=1&b=2') so the caller can
 * choose whether to treat those as queries or as opaque text.
 */
export interface SplitUrl {
  origin: string;
  path: string;
  query: { key: string; value: string }[];
  hash: string;
}

export function splitUrlAndQuery(raw: string): SplitUrl | null {
  // We only run the WHATWG parser when there's something that looks
  // like a scheme; otherwise URL throws and we return null.
  if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(raw)) return null;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  const query: { key: string; value: string }[] = [];
  u.searchParams.forEach((v, k) => query.push({ key: k, value: v }));
  return {
    origin: u.origin,
    path: u.pathname,
    query,
    hash: u.hash,
  };
}

export function buildQuery(pairs: { key: string; value: string }[]): string {
  const usp = new URLSearchParams();
  for (const { key, value } of pairs) {
    if (key.length === 0) continue;
    usp.append(key, value);
  }
  return usp.toString();
}

function explainUriError(e: unknown): string {
  if (e instanceof URIError) return `URI 错误: ${e.message || '无法解码'}`;
  return e instanceof Error ? e.message : String(e);
}
